/**
 * Auth context: exposes the signed-in user, their resolved role + capabilities, and sign-in/out.
 * Enforces the ad.net domain on every auth state change, and resolves the role from the Firestore
 * directory (falling back to the bootstrap list if Firestore is unreachable/not set up yet).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import {
  auth, AUTH_EMULATOR_ACTIVE, DOMAIN_RESTRICTION_MESSAGE, isAllowedUser, signInWithGoogle, signOutUser,
} from "./firebase";
import {
  effectiveRoles, fetchStoredRoles, isBootstrapAdmin, canViewWithRoles, rolesCan, upsertUserOnLogin,
  type Capability, type Role,
} from "./roles";

interface AuthState {
  user: User | null;
  roles: Role[];
  isAdmin: boolean;
  can: (cap: Capability) => boolean;
  canView: (view: string) => boolean;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (u && !isAllowedUser(u)) {
        await signOutUser();
        setUser(null);
        setRoles([]);
        setError(DOMAIN_RESTRICTION_MESSAGE);
        setLoading(false);
        return;
      }
      if (u) {
        // Resolve roles. If Firestore isn't reachable yet (e.g. DB not created), fall back to the
        // bootstrap list so an admin can still get in and finish setup. In local emulator mode there
        // is NO Firestore emulator, so the real-Firestore call would HANG on the emulator token and
        // block setUser — skip it and use the bootstrap fallback so the local login completes.
        if (AUTH_EMULATOR_ACTIVE) {
          setRoles(isBootstrapAdmin(u.email) ? ["admin"] : []);
        } else {
          try {
            await upsertUserOnLogin(u.email!);
            setRoles(effectiveRoles(u.email, await fetchStoredRoles(u.email!)));
          } catch {
            setRoles(isBootstrapAdmin(u.email) ? ["admin"] : []);
          }
        }
        setUser(u);
        setError(null);
      } else {
        setUser(null);
        setRoles([]);
      }
      setLoading(false);
    });
  }, []);

  const signIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (e) {
      const code = (e as { code?: string })?.code ?? "";
      const msg = e instanceof Error ? e.message : "Sign-in failed. Please try again.";
      if (/popup-closed|cancelled|canceled/i.test(code + msg)) return; // user dismissed the popup
      // Self-signup is disabled — only pre-provisioned accounts may sign in.
      if (code === "auth/admin-restricted-operation") {
        setError("This account isn't set up for Cortex yet. Ask an admin to add you.");
      } else {
        setError(msg);
      }
    }
  };

  const signOut = async () => {
    await signOutUser();
    setUser(null);
    setRoles([]);
  };

  const value: AuthState = {
    user, roles, loading, error, signIn, signOut,
    isAdmin: roles.includes("admin"),
    can: (cap) => rolesCan(roles, cap),
    canView: (view) => canViewWithRoles(roles, view),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Whether the signed-in user holds a capability, for a view that also renders outside <AuthProvider> (the internal report's tests
 *  and fixtures preview): no provider means no capability, so an action it gates stays hidden. */
export function useCan(cap: Capability): boolean {
  return useContext(AuthContext)?.can(cap) ?? false;
}

/** Who is signed in, as a stable key for a per-person browser setting (uid, else email); null with no provider or no one signed in. */
export function useUserKey(): string | null {
  const u = useContext(AuthContext)?.user;
  return u ? (u.uid || u.email || null) : null;
}
